import React, {useEffect, useRef, useState} from 'react';
import {Box, Flex} from '@chakra-ui/react';
import dayjs from 'dayjs';
import BackButton from '../../components/BackButton';
import {PinkButton} from '../../components/button';
import {TextG16, TextG18, TextG24} from '../../components/typography';
import {ToastMessage} from '../../components/toast';
import useAuth from '../../hooks/useAuth';
import {cancelSubscription} from '../../endpoints/api';
import {logSubscriptionCancel} from '../../utils/analytics';
import useTrackEvent from '../../hooks/useTrackEvent';
import PauseConfirmModal from '../../components/Modals/PauseConfirmModal';
import PerkCarousel from './components/PerkCarousel';
import LossList from './components/LossList';
import {useNavigate} from 'react-router-dom';

const FALLBACK_VALUE = 253;
export const PAUSE_SLIDE_DURATION_MS = 280;

const DEFAULT_CANCEL_REASON_ID = 1;

export type PauseSource = 'pause_button' | 'cancel_button';

type Props = {
  isOpen: boolean;
  source: PauseSource;
  onClose: () => void;
};

const MembershipPause = ({isOpen, source, onClose}: Props) => {
  const navigate = useNavigate();
  const {user, reFetchUserData} = useAuth();
  const {addUserEvent} = useTrackEvent();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  // mounted controls DOM presence; visible drives the slide-in transform.
  const [mounted, setMounted] = useState(isOpen);
  const [visible, setVisible] = useState(false);
  const enterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeMembershipPlanSubscription = user?.activeMembershipPlanSubscription;
  const paidThroughDate = activeMembershipPlanSubscription?.paid_through_date;

  // Log intercept impression each time the overlay opens.
  useEffect(() => {
    if (isOpen) {
      addUserEvent({
        type: 'view',
        event_name: 'cancel_intercept_view',
        data: {source},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, source]);

  // Drive enter/exit animations from the isOpen prop.
  useEffect(() => {
    if (enterTimer.current) clearTimeout(enterTimer.current);
    if (exitTimer.current) clearTimeout(exitTimer.current);

    if (isOpen) {
      setMounted(true);
      enterTimer.current = setTimeout(() => setVisible(true), 20);
    } else if (mounted) {
      setVisible(false);
      exitTimer.current = setTimeout(() => setMounted(false), PAUSE_SLIDE_DURATION_MS);
    }

    return () => {
      if (enterTimer.current) clearTimeout(enterTimer.current);
      if (exitTimer.current) clearTimeout(exitTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleKeep = (sourceSection: 'intercept' | 'modal') => {
    addUserEvent({
      type: 'click',
      event_name: 'cancel_intercept_keep_clicked',
      data: {source: sourceSection},
    });
    if (sourceSection === 'modal') {
      setConfirmOpen(false);
    }
    onClose();
  };

  const handleBack = () => {
    addUserEvent({
      type: 'click',
      event_name: 'cancel_intercept_back_clicked',
    });
    onClose();
  };

  const handleOpenConfirm = () => {
    addUserEvent({
      type: 'click',
      event_name: 'cancel_intercept_continue_clicked',
    });
    setConfirmOpen(true);
  };

  const handleContinueToCancel = async () => {
    setIsCancelling(true);
    try {
      await cancelSubscription({reasonId: DEFAULT_CANCEL_REASON_ID});
      addUserEvent({
        type: 'click',
        event_name: 'cancel_confirmed',
        data: {subscription_id: activeMembershipPlanSubscription?.id},
      });
      logSubscriptionCancel(activeMembershipPlanSubscription);
      await reFetchUserData();
      setConfirmOpen(false);
      navigate('/membership/pause/success', {
        state: {
          paidThroughDate,
        },
      });
    } catch (err: any) {
      ToastMessage({
        status: 'error',
        text: err?.message || 'Failed to cancel membership',
      });
      setIsCancelling(false);
    }
  };

  const resumeOnDate = paidThroughDate ? dayjs(paidThroughDate).format('YYYY-MM-DD') : undefined;

  if (!mounted) return null;

  return (
    <Box position="fixed" top={0} left={0} right={0} bottom={0} zIndex={20}>
      {/* Sliding page content (behind the static footer) */}
      <Box
        //pb="220px"
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="white"
        overflowY="auto"
        transform={visible ? 'translateX(0)' : 'translateX(100%)'}
        transition={`transform ${PAUSE_SLIDE_DURATION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`}
        willChange="transform"
        boxShadow="-8px 0 24px rgba(0,0,0,0.08)">
        <Flex align="center" px={4} pt={`16px`} pb={2}>
          <BackButton onClick={handleBack} sticky={false} />
        </Flex>

        <Box px={5}>
          <Box mb="16px" fontWeight={700} lineHeight="36px" color="#202020" letterSpacing="-0.02em">
            <TextG24>Making Sure You Don&rsquo;t Miss Out&hellip;</TextG24>
          </Box>
        </Box>
        <Box
          minH="100vh"
          p="16px"
          boxShadow="0 4px 16px 0 rgba(121, 121, 141, 0.20), 0 5px 30px 0 rgba(121, 121, 141, 0.20)">
          {/* <TextG16 color="#4D4D4D" mt={2} lineHeight="23px">
            We&rsquo;ve got you covered. Take a break &mdash; without losing your membership.
          </TextG16> */}

          <Box mt={6}>
            <TextG18 fontWeight={700} color="#202020">
              What you&rsquo;ll miss if you cancel
            </TextG18>
            <Flex align="baseline" mt={1} gap={2}>
              <Box fontWeight={700} color="#202020">
                <TextG24>${FALLBACK_VALUE}</TextG24>
              </Box>
              <TextG16 color="#4D4D4D" fontWeight={500}>
                value so far
              </TextG16>
            </Flex>
          </Box>

          <Box mt={4} mx={-5}>
            <PerkCarousel />
          </Box>

          <Box mt={6}>
            <TextG18 fontWeight={700} color="#202020">
              You&rsquo;ll lose access to:
            </TextG18>
            <Box mt={3}>
              <LossList />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Static footer */}
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        bg="white"
        px={5}
        pt={3}
        pb={9}
        display="flex"
        flexDirection="column"
        gap={3}
        alignItems="center"
        boxShadow="0 -4px 20px rgba(0,0,0,0.04)"
        zIndex={5}>
        <PinkButton
          h="48px"
          borderRadius="100px"
          width="100%"
          boxShadow="0 -4px 0 0 rgba(0, 0, 0, 0.25) inset"
          onClick={() => handleKeep('intercept')}>
          <TextG16 fontWeight="500">Keep Membership</TextG16>
        </PinkButton>
        <Box
          as="button"
          onClick={handleOpenConfirm}
          bg="transparent"
          border="none"
          cursor="pointer"
          color="#262626"
          fontWeight={500}
          lineHeight="24px"
          textDecoration="underline"
          textUnderlineOffset="auto"
          p={1}
          opacity={visible ? 1 : 0}
          transition={`opacity ${PAUSE_SLIDE_DURATION_MS}ms ease-in-out`}
          pointerEvents={visible ? 'auto' : 'none'}>
          <TextG16>Continue to cancel</TextG16>
        </Box>
      </Box>

      <PauseConfirmModal
        isOpen={confirmOpen}
        onClose={() => !isCancelling && setConfirmOpen(false)}
        onKeep={() => handleKeep('modal')}
        onContinue={handleContinueToCancel}
        resumeOnDate={resumeOnDate}
        isLoading={isCancelling}
      />
    </Box>
  );
};

export default MembershipPause;
